<template>
    <div class="card card-height">
        <div class="table-responsive">
            <table class="table custom-table-padding">
                <thead>
                    						
                    <tr>
                        <th>Photo</th>
                        <th class="patient-details">Patient Details</th>
                        <th class="admission-no">
                            <span class="d-block">Admission No |</span>
                            <span class="d-block">Date & Time</span>
                        </th>
                        <th>Bed / Word Name</th>
                        <th>Consultant</th>
                        <th>
                            <span class="d-block">Reference ID</span>
                        </th>
                        <th>
                            <span class="d-block">CD No</span>
                        </th>
                        <th class="action-th">Report</th>
                        <th class="action-th">Discharge</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, i) in tableItems" :key="i">

                        <td>
                            <img :src=item.patient_photo alt="avatar" class="profile-pic">
                        </td>

                        <td> 
                            <div class="col">
                                <strong>
                                    <h4>{{ item.patient_name }} {{ formatPatientInfo(item) }} </h4>
                                </strong>
                                {{ formatPatientAdditionalInfo(item) }}
                            </div>
                        </td>

                        <td>
                            <div class="col">
                                <h5 class="text-bold">{{ item.admission_no }}</h5>
                               <span>{{ item.admission_date }}</span> 
                            </div>
                        </td>

                        <td>
                            {{ item.bed }}
                        </td>

                        <td>
                            {{ item.consultant }}
                        </td>

                        <td>
                            {{ item.meta_data && item.meta_data.master_system_identifier }}
                        </td>

                        <td>
                            {{ item.meta_data && item.meta_data.media_cd_no }}
                        </td>

                        <td>
                            <div class="row justify-content-center">
                                <div v-if="item.registration_status === 'admitted'"
                                    class="col-3 print-btn">
                                    <button @click="onClickEdit(item.id, item.service_general_id, 'admission')" class="btn btn-primary btn-sm">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                </div>
                                <div class="col-3">
                                    <button 
                                        :disabled="pdfGenerating[item.id]" 
                                        @click="goToPrint(item, 1)" class="btn btn-secondary btn-sm">
                                        <i class="fas fa-print"></i>
                                    </button>
                                </div>
                            </div>
                        </td>

                        <td>
                            <div class="d-flex justify-content-between">
                                <div class="col-3 btn-sm">
                                    <button @click="onClickEdit(item.id, item.service_general_id, 'discharge')" class="btn btn-primary btn-sm">
                                        Discharge
                                    </button>
                                </div>
                                <div v-if="item.registration_status === 'released'" class="col-3 d-flex align-items-center">
                                    <button 
                                        :disabled="pdfGenerating[item.id]" 
                                        @click="goToPrint(item, 2)" class="btn btn-secondary btn-sm">
                                        <i class="fas fa-print"></i>
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
    </div>
</div>
</template>

<script setup>
    import { reactive } from 'vue'
    import { useRoute, useRouter } from "vue-router";
    import { dateOfBirthConvertToAge } from '@/services/utils/global.js';
    import { lowerCase } from 'lodash';
    import cagPdfHelper from "@/services/utils/pdf/cagPdfHelper.js"; 
    import cagDischargePdfHelper from "@/services/utils/pdf/cagDischargePdfHelper.js"; 
    import handleHospital from '@/services/modules/hospital';

    const { generatePdf } = cagPdfHelper();
    const { generatePdf: generatePdfDischarge } = cagDischargePdfHelper();
    const { fetchSingleNote } = handleHospital();
    const $route = useRoute();
    const $router = useRouter();
    const pdfGenerating = reactive({});
    
    const props = defineProps({
        tableItems: {
            type: Array,
        }
    })

    const onClickEdit = (id, serviceId, routeName) => {
        $router.push({name: `edit-cag-pci-${routeName}`,
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId,
                admissionId: id,
                serviceId: serviceId
            },
            query: $route.query,
        })
    }

    const formatPatientInfo = (item) => {
        let text = '';
        if(!item.patient_birthday && !item.patient_gender) return text;

        if(item.patient_birthday){
            const { year } = dateOfBirthConvertToAge(item.patient_birthday);
            text =+ year + 'years';
        }

        if(item.patient_birthday && item.patient_gender) text += ', ';

        if(item.patient_gender){
            const gender = lowerCase(item.patient_gender);
            text += `${gender}`;
        }
        
        return `(${text})`;
    }

    const formatPatientAdditionalInfo = (item) => {
        let text = '';
        if(item.mobile) text += item.mobile;
        if((item.mobile && item.id)) text += ' | ';
        if(item.serial_no) text += item.serial_no;
        return text;
    }

    const goToPrint = async (item, btnFunc) => {
        if (pdfGenerating[item.id]) return;
        pdfGenerating[item.id] = true;

        try {
            switch (btnFunc) {
                case 1:
                    await generatePdf(item.id, $route.params.companyId);
                    break;
                
                case 2:
                    const addQuery = `?company_id=${$route.params.companyId}&search_type=all&type=cag`;
                    const res = await fetchSingleNote(addQuery, item.id);

                    if (!res.status) {
                      showError(res.message);
                      return;
                    }
                  
                    await generatePdfDischarge(res.data, item.service_general_id, $route.params.companyId);
                    break;
                
                default:
                    break;
            }
        } catch (error) {
            console.error(error);
        } finally {
            pdfGenerating[item.id] = false;
        }
    };
</script>

<style scoped>

.admission-no{
    width: 15%;
}
.patient-details{
    width: 25%;
}
.print-btn{
    margin-right: 10px;
}
.action-th{
    width: 15%;
    text-align: center;
}
.profile-pic{
    width: 65px;
}
</style>